import type { IOrdersEdgesNode } from "../interface/customer";
import type { ICaaLineItem, ICaaOrder, ICaaTrackingInformation } from "../interface/customer-account";
import type { ILineItem, IReturnLineItem } from "../interface/order";
import type { IProductDetailQuery, IProductMetafields } from "../interface/product";
import { OrderStatus } from "../types/order";
import type {
  AutomaticDiscountApplication,
  CartAutomaticDiscountAllocation,
  CartCodeDiscountAllocation,
  CartCustomDiscountAllocation,
  CartDiscountAllocation,
  DiscountApplication,
  DiscountCodeApplication,
  MailingAddress,
  ManualDiscountApplication,
  Order,
  Product,
  ScriptDiscountApplication,
} from "../types/storefront.types";
import { CountryCode, OrderFinancialStatus, OrderFulfillmentStatus } from "../types/storefront.types";
import { residentRegex } from "./regex";

// fulfillmentStatus
export const OrderStatusTrans: Record<OrderStatus, string> = {
  [OrderStatus.Refunded]: "order_status.refunded", // 환불 완료
  [OrderStatus.Canceled]: "order_status.canceled", // 주문 취소
  [OrderStatus.Partially]: "order_status.partially", // 부분 환불됨
  [OrderStatus.Completed]: "order_status.completed", // 결제 완료
  [OrderStatus.Return_Requested]: "order_status.return", // 반품 접수됨
  [OrderStatus.Return_Approved]: "order_status.return_approved", // 반품 승인됨
  [OrderStatus.Shipped]: "order_status.shipped", // 발송 완료
  [OrderStatus.Partially_Shipped]: "order_status.partially_shipped", // 일부 발송 완료
  [OrderStatus.Partially_Preparing]: "order_status.partially_shipped", // 일부 배송 준비 중 -> 패밀리세일용
  [OrderStatus.Preparing]: "order_status.preparing", // 배송 준비 중
  [OrderStatus.Scheduled]: "order_status.scheduled", // 예약 발송 전
  [OrderStatus.Scheduled_Preparing]: "order_status.scheduled_shipped", // 예약 발송 준비 중
  [OrderStatus.Completed_Preparing]: "order_status.completed_shipped", // 예약 발송 완료
  [OrderStatus.Paid]: "order_status.paid", // 결제 완료
  [OrderStatus.Fulfilled]: "order_status.fulfilled",
  [OrderStatus.Unfulfilled]: "order_status.unfulfilled",
  [OrderStatus.Pending]: "order_status.pending", // 결제 확인 중
  [OrderStatus.PrizeCancel]: "order_status.prizeCancel", // 당첨 취소
  [OrderStatus.DrawCancel]: "order_status.drawCancel", // 응모 취소
  [OrderStatus.DrawLose]: "order_status.drawLose", // 미당첨
  [OrderStatus.DrawWin]: "order_status.drawWin", // 당첨 🎉
  [OrderStatus.DrawApply]: "order_status.drawApply", // 응모 완료
  [OrderStatus.Gifting_Pending]: "order_status.gifting_pending", // 선물 수락 대기중
  [OrderStatus.Gifting_Accepted]: "order_status.gifting_accepted", // 선물 수락 완료
  [OrderStatus.None]: "", // 초기값
};

/** Draw Product를 포함하는 오더인지 확인 */
export const checkDrawOrder = (order?: IOrdersEdgesNode): boolean => {
  if (!order) return false;

  // Draw 태그가 있어도 Onsale 태그가 있으면 일반 상품으로 본다
  return Boolean(order.instagram) || Boolean(order.drawStatus);
};

/** 주문 목록 페이지에서 보여줘야 하는 주문만 필터 */
export const orderHistoryFilter = (order?: IOrdersEdgesNode): boolean => {
  if (!order) return false;
  if (order.isfamilysale) return false;

  // financialStatus가 PENDING인 주문은 보여주지 않는다.
  // Draw 오더이면서 financialStatus가 PAID가 아닌 주문은 보여주지 않는다.
  return !(
    order.financialStatus === OrderFinancialStatus.Pending ||
    (checkDrawOrder(order) &&
      order.financialStatus !== OrderFinancialStatus.Paid &&
      order.financialStatus !== OrderFinancialStatus.Refunded &&
      order.financialStatus !== OrderFinancialStatus.PartiallyRefunded) ||
    order.drawStatus?.value === "lose"
  );
};

/** 주문이 송장번호를 가지고 있는지 확인 */
export const orderHasTracking = (order?: Order): boolean => {
  if (!order) return false;

  // order 데이터 아래에 successfulFulfillment와 trackingInfo가 있으면 송장이 있는 것으로 본다.
  return Boolean(order.successfulFulfillments?.[0]?.trackingInfo[0]);
};

/**
 * OrderHistory 페이지 주문상태
 * @param order - 주문 데이터
 */
export const getOrderStatus = (order: IOrdersEdgesNode): OrderStatus | undefined => {
  const { fulfillmentStatus } = order;

  switch (order.financialStatus) {
    case OrderFinancialStatus.Paid:
      // 고객이 주문 완료 후 아직 비젬 주문 확정 전일 때
      if (fulfillmentStatus === OrderFulfillmentStatus.Unfulfilled) return OrderStatus.Completed;
      else if (fulfillmentStatus === OrderFulfillmentStatus.Fulfilled || fulfillmentStatus === OrderFulfillmentStatus.PartiallyFulfilled) {
        // 고객이 주문 완료 후 Order History 페이지에서 반품 접수했을 때
        if (order.returnStatus?.value === "requested") return OrderStatus.Return_Requested;
        // CS팀에서 반품 승인했을 때
        if (order.returnStatus?.value === "approved") return OrderStatus.Return_Approved;

        // 주문에 프리오더 제품이 존재하고 해당 상품이 송장 전송이 되지 않았을 때
        if (hasPreorder(order) && !orderHasTracking(order)) {
          return OrderStatus.Scheduled_Preparing;
        }

        // 주문에 프리오더 제품이 존재하고 송장 전송까지 되었을 때
        if (hasPreorder(order) && orderHasTracking(order)) {
          return OrderStatus.Completed_Preparing;
        }

        // 고객이 주문 완료 후 비젬에서 송장 전송까지 했을 때
        if (orderHasTracking(order)) return OrderStatus.Shipped;

        // 고객이 주문 완료 후 비젬에서 주문 확정했을 때
        return OrderStatus.Preparing;
      }
      // 고객이 주문 완료 후 '일부 상품'을 hold 했을때
      else if (fulfillmentStatus === OrderFulfillmentStatus.OnHold) {
        // 고객이 주문 완료 후 Order History 페이지에서 반품 접수했을 때
        if (order.returnStatus?.value === "requested") return OrderStatus.Return_Requested;

        // 일부 상품만 비젬에서 송장 전송까지 했을때
        if (orderHasTracking(order)) return OrderStatus.Partially_Shipped;

        // 고객이 주문 완료 후 비젬에서 주문 확정했을 때
        return OrderStatus.Paid;
      }
      return OrderStatus.Paid;
    case OrderFinancialStatus.Refunded:
      // 고객이 반품 신청 후, CS팀에서 승인한 뒤 환불 처리까지 완료했을 때
      if (!order.canceledAt) return OrderStatus.Refunded;
      // 고객 또는 CS팀에서 일반적으로 주문을 취소했을 때
      return OrderStatus.Canceled;
    case OrderFinancialStatus.PartiallyRefunded:
      if (order.returnStatus?.value === "requested") return OrderStatus.Return_Requested;
      if (order.returnStatus?.value === "approved") return OrderStatus.Return_Approved;
      // 고객 또는 CS팀에서 일부 상품만 환불하거나 반품 처리했을 때
      return OrderStatus.Partially;
    default:
  }
};

/**
 * CAA 주문상태
 * classic getOrderStatus의 CAA 대응
 * tracking / preorder 판별을 CAA 스키마에서 할 수 없어 단순화되어 있음 (FULFILLED는 tracking 존재 가정하여 Shipped로 취급)
 */
export const getOrderStatusCaa = (order: ICaaOrder): OrderStatus => {
  const returnStatus = order.returnStatus?.value;
  const isOffline = order.isOffline?.value;

  switch (order.financialStatus) {
    case "REFUNDED":
      return order.cancelReason ? OrderStatus.Canceled : OrderStatus.Refunded;
    case "PARTIALLY_REFUNDED":
      if (order.fulfillmentStatus === "FULFILLED" && returnStatus === "requested") return OrderStatus.Return_Requested;
      if (order.fulfillmentStatus === "FULFILLED" && returnStatus === "approved") return OrderStatus.Return_Approved;
      return OrderStatus.Partially;
    case "VOIDED":
      return OrderStatus.Canceled;
    case "PENDING":
      return OrderStatus.Pending;
    case "PARTIALLY_PAID":
    case "PAID":
    case "AUTHORIZED":
      if (order.fulfillmentStatus === "UNFULFILLED") return OrderStatus.Completed;
      if (order.fulfillmentStatus === "ON_HOLD") return OrderStatus.Completed;
      if (order.fulfillmentStatus === "FULFILLED") {
        if (returnStatus === "requested") return OrderStatus.Return_Requested;
        if (returnStatus === "approved") return OrderStatus.Return_Approved;
        if (isOffline) return OrderStatus.Completed;
        return OrderStatus.Shipped;
      }
      return OrderStatus.None;
    default:
      return OrderStatus.None;
  }
};

/**
 * CAA 주문의 배송 추적 링크
 * CAA는 트래킹 번호 자체(successfulFulfillments.trackingInfo)를 노출하지 않음
 * 대신 statusPageUrl(고객용 주문 상태 페이지)이 배송 추적 정보를 포함한 뷰를 제공하므로 이 페이지로 이동시키는 링크만 제공
 *
 * classic getTrackingInfo의 CAA 대응
 * 링크 노출 조건은 classic orderHasTracking과 정렬 — 실제로 발송이 진행/완료된 상태에서만 표시 (UNFULFILLED / ON_HOLD 등은 미노출)
 */
export const getTrackingUrlCaa = (order: ICaaOrder): string | null => {
  if (!order.statusPageUrl) return null;
  const hasShipmentActivity =
    order.fulfillmentStatus === "FULFILLED" ||
    order.fulfillmentStatus === "PARTIALLY_FULFILLED" ||
    order.fulfillmentStatus === "IN_PROGRESS";
  return hasShipmentActivity ? order.statusPageUrl : null;
};

/**
 * 생성 타입의 `CartDiscountAllocation`/`DiscountApplication`은 인터페이스라 `__typename`이 없음
 * 호출부는 인터페이스 타입으로 넘기고, 여기서 구현체 유니온으로 좁혀 사용
 */
export const getDiscountName = (input?: CartDiscountAllocation | DiscountApplication): string => {
  if (!input) return "";

  const discount = input as
    | CartAutomaticDiscountAllocation
    | CartCodeDiscountAllocation
    | CartCustomDiscountAllocation
    | DiscountCodeApplication
    | ManualDiscountApplication
    | AutomaticDiscountApplication
    | ScriptDiscountApplication;

  if (
    discount.__typename === "CartAutomaticDiscountAllocation" ||
    discount.__typename === "CartCustomDiscountAllocation" ||
    discount.__typename === "ManualDiscountApplication" ||
    discount.__typename === "AutomaticDiscountApplication" ||
    discount.__typename === "ScriptDiscountApplication"
  ) {
    return discount.title;
  }

  if (discount.__typename === "CartCodeDiscountAllocation" || discount.__typename === "DiscountCodeApplication") {
    return discount.code;
  }

  return "";
};

/** 주문한 상품중에 예약발송인 상품을 가지고 있는지 확인 */
export const hasPreorder = (order: IOrdersEdgesNode | null): boolean => {
  if (!order) return false;

  return order.lineItems.edges.some((edge) => (edge.node.variant?.product as IProductDetailQuery["product"])?.tags.includes("Preorder"));
};

/**
 * 주문이 반품 가능한 기간 내에 있는지 확인
 * @param {IOrdersEdgesNode} order - 주문
 * @param {number} refundMaxDate - 반품이 가능한 최대 일수
 * @returns {boolean} - 현재 날짜가 반품 가능 기간 내에 있으면 `true`, 그렇지 않으면 `false`를 반환
 */
export const isReturnPeriodValid = (order: IOrdersEdgesNode, refundMaxDate: number): boolean => {
  // fulfillmentDate로 바꾸기
  const orderDate = new Date(order.processedAt);
  const today = new Date();
  const diffInDays = (today.getTime() - orderDate.getTime()) / (24 * 60 * 60 * 1000);
  return diffInDays < refundMaxDate;
};

/**
 * @description 배송 tracking 정보 조회
 */
export const getTrackingInfo = ({ order, lang }: { order?: IOrdersEdgesNode | null; lang: "ko" | "en" | "jp" }) => {
  if (!order) return;

  const CJ_TRACKING_URL = "https://trace.cjlogistics.com/next/tracking.html?wblNo=";

  const fulfillment = order.successfulFulfillments;
  const info = fulfillment?.[fulfillment.length - 1]?.trackingInfo[0];
  if (info) {
    const trackingUrl = lang === "ko" ? `${CJ_TRACKING_URL}${info.number ?? ""}` : (info.url ?? "");
    return { ...info, url: trackingUrl };
  }

  return null;
};

/**
 * @description Gift 태그가 없는 일반 상품이 먼저, Gift 태그가 있는 상품이 나중에 정렬
 */
export const sortOrderItems = (items: ILineItem[] | IReturnLineItem[]): ILineItem[] | IReturnLineItem[] => {
  return items.sort((a, b) => {
    const aIsGift = a.variant?.product.tags.includes("Gift");
    const bIsGift = b.variant?.product.tags.includes("Gift");
    return Number(aIsGift) - Number(bIsGift);
  });
};

export const RESIDENT_COUNTRY = {
  [CountryCode.Br]: { name: "CPF/CNPJ", key: "SHIPPING_CREDENTIAL_BR" },
  [CountryCode.Cn]: { name: "Resident ID", key: "SHIPPING_CREDENTIAL_CN" },
  [CountryCode.Mx]: { name: "RFC", key: "SHIPPING_CREDENTIAL_MX" },
};

// Resident ID /CPF/CNPJ/RFC 유효성 검사
export const checkResidentId = (residentId: string, address: MailingAddress | undefined): boolean => {
  const countryCode = address?.countryCodeV2;
  if (!countryCode) return false;

  // 브라질
  if (countryCode === CountryCode.Br) {
    const value = residentId.replace(/\D/g, "");

    // CPF
    if (value.length === 11 && residentRegex.CFP.test(value)) {
      return true;
    }

    // CNPJ
    if (value.length === 14 && residentRegex.CNPJ.test(value)) {
      return true;
    }

    return false;
  }

  // 중국
  if (countryCode === CountryCode.Cn) return residentRegex.CN.test(residentId);

  // 멕시코
  if (countryCode === CountryCode.Mx) return residentRegex.RFC.test(residentId);

  return true;
};

/** CAA 주문의 최신 fulfillment 송장 정보. classic getTrackingInfo의 CAA 대응. */
export const getTrackingInfoCaa = (order?: ICaaOrder | null): ICaaTrackingInformation | null => {
  if (!order) return null;

  const fulfillment = order.fulfillments?.edges;
  const info = fulfillment?.[fulfillment.length - 1]?.node.trackingInformation[0];

  return info ?? null;
};

/** CAA 주문이 송장번호를 가지고 있는지 확인. classic orderHasTracking의 CAA 대응. */
export const orderHasTrackingCaa = (order?: ICaaOrder | null): boolean => Boolean(getTrackingInfoCaa(order));

/** CAA 라인의 productId 목록 (중복 제거) — 상품 태그/정보를 Storefront API로 따로 조회할 때 사용 */
export const getCaaLineProductIds = (order?: ICaaOrder | null): string[] => [
  ...new Set((order?.lineItems.edges ?? []).map(({ node }) => node.productId).filter((id): id is string => Boolean(id))),
];

/**
 * 주문한 상품중에 예약발송인 상품을 가지고 있는지 확인. classic hasPreorder의 CAA 대응.
 * CAA 라인엔 상품 태그가 없어, Storefront API로 조회한 productId → tags 맵을 받아 판별한다 (useCaaProductTags)
 * 맵이 없으면 판별할 수 없어 false
 */
export const hasPreorderCaa = (order: ICaaOrder | null, tagsByProductId?: Map<string, string[]>): boolean => {
  if (!order || !tagsByProductId) return false;

  return order.lineItems.edges.some(({ node }) => Boolean(node.productId && tagsByProductId.get(node.productId)?.includes("Preorder")));
};

/**
 * CAA 라인의 할인 적용 후 금액 (classic discountedTotalPrice 대응)
 * currentTotalPrice는 환불/취소되면 그만큼 줄어(전체 환불이면 0) 쓸 수 없어,
 * 환불 후에도 유지되는 할인 전 단가(price) × 주문 수량에서 라인에 배분된 할인을 빼서 구함
 */
const getCaaLineDiscountedPrice = (node: ICaaLineItem): string => {
  if (!node.price) return node.totalPrice?.amount || "0";

  const discountAmount = node.discountAllocations.reduce((sum, item) => sum + parseFloat(item.allocatedAmount.amount || "0"), 0);

  return String(parseFloat(node.price.amount) * node.quantity - discountAmount);
};

/** Storefront API로 따로 조회한 현재 상품 데이터 (fetchProductsByIds) */
export type TCaaLineProduct = Product & IProductMetafields;

/**
 * CAA LineItem을 classic OrderLineItem 형태로 매핑 — useOrderLineInfo가 classic과 같은 결과를 내도록
 * - Size/Color: variantOptions → selectedOptions
 * - product가 있으면(Storefront로 조회한 현재 상품) classic 라인의 variant와 같은 값으로 채움
 *   — classic 라인의 variant도 주문 당시가 아닌 현재 상품 데이터라 결과가 같음
 *   (compareAtPrice 정가 취소선, tags의 Gift/Preorder 판별, notice 프리오더 문구)
 * - product가 없으면(삭제된 상품 등) CAA 라인만으로 채운다 — 정가는 할인 전 단가(price), 사은품은 vendor(GWP)만 판별
 */
export const toCaaOrderLineItem = (node: ICaaLineItem, product?: TCaaLineProduct | null): IReturnLineItem => {
  const variant = product?.variants.edges.find((edge) => edge.node.id === node.variantId)?.node;

  return {
    id: node.id,
    title: node.title,
    quantity: node.quantity,
    originalQuantity: node.quantity,
    discountedTotalPrice: { amount: getCaaLineDiscountedPrice(node) },
    discountAllocations: [],
    returnReason: "",
    customerNote: "",
    variant: {
      id: node.variantId || "",
      title: node.variantTitle || "",
      price: { amount: variant?.price.amount || node.price?.amount || "0" },
      compareAtPrice: variant?.compareAtPrice ?? null,
      image: { url: node.image?.url || "", width: 1280, height: 1707 },
      selectedOptions: (node.variantOptions ?? []).map(({ name, value }) => ({ name, value })),
      product: product
        ? { ...product, vendor: product.vendor || node.vendor || "" }
        : { tags: [], vendor: node.vendor || "", handle: "", title: node.title, status: "" },
    },
  } as unknown as IReturnLineItem;
};

/**
 * classic sortOrderItems(사은품을 뒤로)의 CAA 대응
 * classic은 "Gift" 태그로만 판별하지만 CAA 라인(toCaaOrderLineItem)엔 태그가 없을 수 있어 vendor(GWP)도 함께 봄
 * — admin API fallback 데이터(태그 있음)와 CAA 라인 모두 같은 기준으로 정렬됨
 */
export const sortOrderItemsCaa = <T extends ILineItem | IReturnLineItem>(items: T[]): T[] => {
  const isGift = (item: T) => Boolean(item.variant?.product.tags.includes("Gift") || item.variant?.product.vendor === "GWP");
  return [...items].sort((a, b) => Number(isGift(a)) - Number(isGift(b)));
};
